import { useState, useMemo, useEffect, useCallback } from 'react';
import type { Deal } from '../types';
import { fetchEligibleCounts, fetchCachedDenominators } from '../utils/database';
import { friendlyTime } from '../utils/friendlyTime';

// ─── types ───────────────────────────────────────────────────────────────────

interface CellData {
  num: number;
  den: number;
  pct: number; // 0–100, or -1 if no data
}
interface SolutionRow {
  solution: string;
  cells: Record<string, CellData>;
  total: CellData;
}
interface FocusGroup {
  focus: string;
  solutions: SolutionRow[];
}
interface HeatmapData {
  segments: string[];
  focusGroups: FocusGroup[];
  totals: Record<string, CellData>;
  grandTotal: CellData;
}

// solution → market_area → count
type DenomMap = Map<string, Map<string, number>>;
interface Props {
  deals: Deal[];
  onClose: () => void;
}

// ─── helpers ─────────────────────────────────────────────────────────────────

function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}
const SOLUTION_BLOCKLIST = new Set(['marketo']);
const SOLUTION_RENAME: Record<string, string> = {
  'aem-assets': 'Assets',
  'aem-sites': 'Sites',
  'aem-forms': 'Forms',
  'marketo-engage': 'Engage',
  'genstudio': 'Performance Marketing (GSPeM)',
  'workfront': 'WKFT',
  'workfront-workflow': 'WKFT Workflow',
  'commerce': 'Commerce'
};

// Fixed focus groups and solution display order. Solutions absent from this list
// are shown under 'Others' at the bottom.
const FOCUS_SOLUTION_ORDER: ReadonlyArray<{
  focus: string;
  solutions: readonly string[];
}> = [{
  focus: 'D&I',
  solutions: ['CJA', 'CJA B2B', 'Analytics', 'Mix Modeler', 'Content Analytics']
}, {
  focus: 'P&A',
  solutions: ['AEP', 'RTCDP', 'Collaboration']
}, {
  focus: 'CJM',
  solutions: ['AJO', 'Campaign', 'Exp Accelerator', 'Target', 'AJO B2B Edition']
}, {
  focus: 'Marketo',
  solutions: ['Engage', 'Measure']
}, {
  focus: 'AI',
  solutions: ['Agent Orchestrator']
}, {
  focus: 'AEM',
  solutions: ['Sites', 'Assets', 'Forms', 'Sites Optimizer', 'LLM Optimizer', 'Learning Manager']
}, {
  focus: 'Genstudio',
  solutions: ['Performance Marketing (GSPeM)']
}];
const SOLUTION_TO_FOCUS = new Map<string, string>(FOCUS_SOLUTION_ORDER.flatMap(({
  focus,
  solutions
}) => solutions.map(s => [s, focus])));
const FOCUS_ORDER = FOCUS_SOLUTION_ORDER.map(f => f.focus);
const SOLUTION_ORDER_IN_FOCUS = new Map<string, Map<string, number>>(FOCUS_SOLUTION_ORDER.map(({
  focus,
  solutions
}) => [focus, new Map(solutions.map((s, i) => [s, i]))]));

// Solution names whose slugified form doesn't match their report config ID suffix.
const SOLUTION_REPORT_SLUG_OVERRIDE: Record<string, string> = {
  'LLM Optimizer': 'llmo'
};
function parseProducts(products: string): Array<{
  focus: string;
  solution: string;
}> {
  if (!products) return [];
  return products.split(',').map(p => {
    const t = p.trim();
    if (!t) return null;
    const idx = t.indexOf(':');
    const raw = (idx === -1 ? t : t.substring(idx + 1)).trim();
    const slug = slugify(raw);
    if (SOLUTION_BLOCKLIST.has(slug)) return null;
    const solution = SOLUTION_RENAME[slug] ?? raw;
    if (!solution) return null;
    // Focus comes from the fixed mapping, not the WF product prefix.
    const focus = SOLUTION_TO_FOCUS.get(solution) ?? 'Others';
    return {
      focus,
      solution
    };
  }).filter((x): x is {
    focus: string;
    solution: string;
  } => x !== null && x.solution.length > 0);
}
function makeCell(num: number, den: number): CellData {
  if (num === 0 && den === 0) return {
    num: 0,
    den: 0,
    pct: -1
  };
  if (den === 0) return {
    num,
    den: 0,
    pct: -1
  };
  return {
    num,
    den,
    pct: num / den * 100
  };
}
function cellBg(cell: CellData): React.CSSProperties {
  if (cell.pct < 0) return {};
  if (cell.den < 5) return {
    background: '#F3F4F6',
    color: '#9CA3AF'
  };
  const p = cell.pct;
  if (p >= 80) return {
    background: '#15803D',
    color: '#fff'
  };
  if (p >= 60) return {
    background: '#16A34A',
    color: '#fff'
  };
  if (p >= 40) return {
    background: '#FCD34D',
    color: '#78350F'
  };
  if (p >= 20) return {
    background: '#EF4444',
    color: '#fff'
  };
  return {
    background: '#B91C1C',
    color: '#fff'
  };
}

// ─── Column key ───────────────────────────────────────────────────────────────
// Which Deal field drives heatmap columns (and denominator lookups).
const HEATMAP_COLUMN_KEY: keyof Deal = 'segment';

// Only these values may appear as heatmap columns — order here defines display order.
const ALLOWED_COLUMNS = ['India (Key)', 'Canada', 'Enterprise', 'Scale', 'Public Sector', 'UKI', 'ME', 'SSA', 'Central', 'Western', 'ANZ', 'Asia', 'India (Japan)', 'Japan', 'No Value'];
const ALLOWED_COLUMNS_SET = new Set(ALLOWED_COLUMNS);
const CENTRAL_AREAS = new Set(['Germany', 'Austria', 'Switzerland', 'Bulgaria', 'Czech Republic', 'Estonia', 'Greece', 'Hungary', 'Poland', 'Romania', 'Russia', 'Slovenia', 'Slovakia', 'Turkey', 'Ukraine']);
const WESTERN_AREAS = new Set(['Belgium', 'Denmark', 'Spain', 'Finland', 'France', 'Italy', 'Lithuania', 'Latvia', 'Luxembourg', 'Netherlands', 'Norway', 'Portugal', 'Sweden']);

// Special routing rules: certain deals are redirected to a named column
// instead of their natural HEATMAP_COLUMN_KEY value.
// Rules are evaluated in order — first match wins.
// Returns undefined when the resolved column is not in the allowlist (deal excluded).
function resolveColumnKey(deal: Deal, columnKey: keyof Deal): string | undefined {
  const ma = deal.market_area;
  if (ma === 'Canada') return 'Canada';
  if (ma === 'United Kingdom') return 'UKI';
  if (ma === 'Middle East') return 'ME';
  if (ma === 'SSA & Israel') return 'SSA';
  if (ma && CENTRAL_AREAS.has(ma)) return 'Central';
  if (ma && WESTERN_AREAS.has(ma)) return 'Western';
  if (ma === 'ANZ') return 'ANZ';
  if (ma === 'Asia') return 'Asia';
  if (ma === 'Japan') return 'Japan';
  if (ma === 'India' && deal.segment_sales === 'Key') return 'India (Key)';
  const raw = deal[columnKey] as string | undefined;
  const col = raw && raw.trim() ? raw.trim() : 'No Value';
  return ALLOWED_COLUMNS_SET.has(col) ? col : undefined;
}

// Columns that must always appear even when no deals land there.
const FORCED_COLUMNS = ALLOWED_COLUMNS;

// ─── computeHeatmap ───────────────────────────────────────────────────────────
// Numerator: count deals by (focus||solution, resolvedColumn).
// Denominator: from denomMap when available (live totals), otherwise dummyDen.
// Columns: union of resolved column values from deals + denomMap keys.

function computeHeatmap(deals: Deal[], denomMap: DenomMap | null, columnKey: keyof Deal = HEATMAP_COLUMN_KEY): HeatmapData {
  const numMap = new Map<string, Map<string, number>>();
  const segSet = new Set<string>();
  const keyOrder: string[] = [];
  for (const deal of deals) {
    const seg = resolveColumnKey(deal, columnKey);
    if (seg) segSet.add(seg);
    for (const {
      focus,
      solution
    } of parseProducts(deal.products)) {
      const key = `${focus}||${solution}`;
      if (!numMap.has(key)) {
        numMap.set(key, new Map());
        keyOrder.push(key);
      }
      if (seg) {
        const m = numMap.get(key)!;
        m.set(seg, (m.get(seg) || 0) + 1);
      }
    }
  }

  // Always show forced columns even when no deals land there
  for (const col of FORCED_COLUMNS) segSet.add(col);

  // Add keys from denomMap so 0%-covered segments become visible columns
  if (denomMap) {
    for (const [, mMap] of denomMap) {
      for (const seg of mMap.keys()) segSet.add(seg);
    }
  }
  const segments = ALLOWED_COLUMNS.filter(c => segSet.has(c));
  const focusMap = new Map<string, SolutionRow[]>();
  const focusOrder: string[] = [];
  for (const key of keyOrder) {
    const sepIdx = key.indexOf('||');
    const focus = key.substring(0, sepIdx);
    const solution = key.substring(sepIdx + 2);
    if (!focusMap.has(focus)) {
      focusMap.set(focus, []);
      focusOrder.push(focus);
    }
    const segCounts = numMap.get(key)!;
    const cells: Record<string, CellData> = {};
    let totalNum = 0;
    let totalDen = 0;
    for (const seg of segments) {
      const num = segCounts.get(seg) || 0;
      const den = denomMap?.get(solution)?.get(seg) ?? 0;
      cells[seg] = makeCell(num, den);
      // Only include segments that have a configured denominator query — segments
      // without one (ANZ, Asia, etc.) would inflate the numerator without a
      // corresponding denominator.
      if (denomMap?.get(solution)?.has(seg)) {
        totalNum += num;
        totalDen += den;
      }
    }
    focusMap.get(focus)!.push({
      solution,
      cells,
      total: makeCell(totalNum, totalDen)
    });
  }

  // Sort focus groups by defined order; 'Others' always last.
  const seenFocuses = new Set(focusOrder);
  const orderedFocuses = [...FOCUS_ORDER.filter(f => seenFocuses.has(f)), ...(seenFocuses.has('Others') ? ['Others'] : []), ...focusOrder.filter(f => f !== 'Others' && !FOCUS_ORDER.includes(f))];
  for (const [focus, rows] of focusMap) {
    const orderMap = SOLUTION_ORDER_IN_FOCUS.get(focus);
    if (orderMap) {
      rows.sort((a, b) => {
        const ai = orderMap.get(a.solution) ?? Infinity;
        const bi = orderMap.get(b.solution) ?? Infinity;
        return ai - bi;
      });
    }
  }
  const focusGroups: FocusGroup[] = orderedFocuses.map(f => ({
    focus: f,
    solutions: focusMap.get(f)!
  }));

  // Totals row: mirror the Total-column fix — only include a solution's contribution
  // to tNum when it has a configured denominator for that segment, so numerator and
  // denominator stay on the same scale.
  const totals: Record<string, CellData> = {};
  for (const seg of segments) {
    let tNum = 0,
      tDen = 0;
    for (const {
      solutions
    } of focusGroups) {
      for (const row of solutions) {
        if (denomMap?.get(row.solution)?.has(seg)) {
          tNum += row.cells[seg]?.num ?? 0;
        }
        tDen += row.cells[seg]?.den ?? 0;
      }
    }
    totals[seg] = makeCell(tNum, tDen);
  }

  // Grand total: sum of the already-fixed solution row Total cells so the scale
  // is consistent with individual rows (both count per-solution occurrences).
  let gNum = 0,
    gDen = 0;
  for (const {
    solutions
  } of focusGroups) {
    for (const row of solutions) {
      gNum += row.total.num;
      gDen += row.total.den;
    }
  }
  return {
    segments,
    focusGroups,
    totals,
    grandTotal: makeCell(gNum, gDen)
  };
}

// ─── Spinner ──────────────────────────────────────────────────────────────────

function Spinner({
  size = 20,
  color = '#6B7280'
}: {
  size?: number;
  color?: string;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" className="animate-spin" style={{
    color
  }} data-id="element-115">
      <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" data-id="element-116" />
      <path d="M12 2a10 10 0 0 1 10 10" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" data-id="element-117" />
    </svg>;
}

// ─── Cell ─────────────────────────────────────────────────────────────────────

function Cell({
  cell
}: {
  cell: CellData;
}) {
  if (cell.pct < 0) return <span style={{
    color: '#9CA3AF',
    fontSize: 10
  }} data-id="element-118">—</span>;
  const cs = cellBg(cell);
  const greyed = cell.den < 5;
  return <div style={{
    ...cs,
    display: 'inline-block',
    padding: '3px 5px',
    borderRadius: 4,
    textAlign: 'center',
    lineHeight: 1.35,
    minWidth: 46
  }} data-id="element-119">
      <div style={{
      fontSize: 10,
      fontWeight: 700
    }} data-id="element-120">{greyed ? '—' : `${Math.round(cell.pct)}%`}</div>
      <div style={{
      fontSize: 9,
      fontWeight: 400,
      opacity: greyed ? 0.6 : 0.85
    }} data-id="element-121">{cell.num}/{cell.den}</div>
    </div>;
}

// ─── styles ───────────────────────────────────────────────────────────────────

const TH: React.CSSProperties = {
  background: '#374151',
  color: '#F9FAFB',
  fontWeight: 600,
  fontSize: 10,
  textAlign: 'center',
  padding: '7px 8px',
  border: '1px solid #4B5563',
  whiteSpace: 'nowrap'
};
const TD: React.CSSProperties = {
  border: '1px solid #E5E7EB',
  padding: '5px 7px',
  textAlign: 'center',
  verticalAlign: 'middle'
};

// ─── HeatmapModal ─────────────────────────────────────────────────────────────

export function HeatmapModal({
  deals,
  onClose
}: Props) {
  const [denomMap, setDenomMap] = useState<DenomMap | null>(null);
  const [isLoadingCache, setIsLoadingCache] = useState(true);
  const [isLoadingTotals, setIsLoadingTotals] = useState(false);
  const [totalsError, setTotalsError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState(false);
  const data = useMemo(() => computeHeatmap(deals, denomMap, HEATMAP_COLUMN_KEY), [deals, denomMap]);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // On mount: load last-persisted denominators from DB (fast, no WF calls).
  useEffect(() => {
    setIsLoadingCache(true);
    void (async () => {
      const solutions = FOCUS_SOLUTION_ORDER.flatMap(({
        solutions
      }) => [...solutions]);
      const settled = await Promise.allSettled(solutions.map(async solution => {
        const reportSlug = SOLUTION_REPORT_SLUG_OVERRIDE[solution] ?? slugify(solution);
        const reportId = `all-eligible-projects-total-${reportSlug}`;
        const result = await fetchCachedDenominators(reportId);
        return {
          solution,
          denominator: result?.heatmapDenominator ?? {},
          lastSyncedAt: result?.lastSyncedAt ?? null
        };
      }));
      const map: DenomMap = new Map();
      let latestSync: string | null = null;
      for (const r of settled) {
        if (r.status === 'fulfilled' && Object.keys(r.value.denominator).length > 0) {
          map.set(r.value.solution, new Map(Object.entries(r.value.denominator)));
          if (r.value.lastSyncedAt && (!latestSync || r.value.lastSyncedAt > latestSync)) {
            latestSync = r.value.lastSyncedAt;
          }
        }
      }
      if (map.size > 0) {
        setDenomMap(map);
        setFromCache(true);
        setLastFetched(latestSync ?? null);
      }
      setIsLoadingCache(false);
    })();
  }, []);

  // Fetch denominator counts for every unique solution in parallel via WF /count API.
  // Report ID pattern: all-eligible-projects-total-<slugified-solution>
  // Each report's countQueries define WF count calls; heatmapDenominator gives market_area → count.
  const handleFetchTotals = useCallback(async () => {
    setIsLoadingTotals(true);
    setTotalsError(null);
    const solutions = FOCUS_SOLUTION_ORDER.flatMap(({
      solutions
    }) => [...solutions]);
    const settled = await Promise.allSettled(solutions.map(async solution => {
      const reportSlug = SOLUTION_REPORT_SLUG_OVERRIDE[solution] ?? slugify(solution);
      const reportId = `all-eligible-projects-total-${reportSlug}`;
      const result = await fetchEligibleCounts(reportId);
      return {
        solution,
        denominator: result?.heatmapDenominator ?? {}
      };
    }));
    const map: DenomMap = new Map();
    let errorCount = 0;
    for (const r of settled) {
      if (r.status === 'fulfilled') {
        const {
          solution,
          denominator
        } = r.value;
        map.set(solution, new Map(Object.entries(denominator)));
      } else {
        errorCount++;
      }
    }
    setDenomMap(map);
    setFromCache(false);
    setLastFetched(new Date().toISOString());
    setIsLoadingTotals(false);
    if (errorCount > 0) {
      setTotalsError(`${errorCount}/${solutions.length} solution(s) failed — partial totals shown`);
    }
  }, [deals]);
  const isLiveTotals = denomMap !== null;
  const noMarketArea = data.segments.length === 0;
  return <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-6 px-4" style={{
    background: 'rgba(0,0,0,0.55)'
  }} onClick={onClose} data-id="element-122">
      <div className="relative bg-white rounded-xl shadow-2xl w-full" style={{
      maxWidth: 1140
    }} onClick={e => e.stopPropagation()} data-id="element-123">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200" data-id="element-124">
          <div className="flex-1 min-w-0" data-id="element-125">
            <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wide" data-id="element-126">
              Coverage Heatmap
            </h2>
            <p className="text-xs text-slate-500 mt-0.5" data-id="element-127">
              OnDemand coverage by solution × market area.
              {isLiveTotals ? fromCache ? <span className="ml-1 text-blue-600 font-medium" data-id="element-128">
                    Showing cached totals{lastFetched ? ` — last synced ${friendlyTime(lastFetched)}` : ''}. Click Fetch Live Totals to refresh.
                  </span> : <span className="ml-1 text-emerald-700 font-medium" data-id="element-129">
                    Live totals loaded {lastFetched ? friendlyTime(lastFetched) : ''}.
                  </span> : <span className="ml-1 text-amber-600 font-medium" data-id="element-130">
                  Click Fetch Live Totals to load coverage percentages.
                </span>}
            </p>
            {totalsError && <p className="text-xs text-red-600 mt-0.5" data-id="element-131">{totalsError}</p>}
            {noMarketArea && !isLoadingTotals && <p className="text-xs text-amber-700 mt-0.5" data-id="element-132">
                No market_area data in deals — add it to the main report fieldMap to see coverage by segment.
              </p>}
          </div>
          <div className="flex items-center gap-2 ml-4 flex-shrink-0" data-id="element-133">
            <button onClick={() => void handleFetchTotals()} disabled={isLoadingTotals || isLoadingCache} className="px-3 py-1.5 text-xs font-semibold rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors whitespace-nowrap flex items-center gap-1.5" data-id="element-134">
              {isLoadingTotals ? <><Spinner size={12} color="#fff" data-id="element-135" /> Fetching…</> : isLiveTotals ? '↺ Refresh Totals' : 'Fetch Live Totals'}
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-base leading-none font-bold" aria-label="Close" data-id="element-136">
              ✕
            </button>
          </div>
        </div>

        {/* Loading overlay — covers table during mount cache fetch or live WF fetch */}
        {(isLoadingCache || isLoadingTotals) && <div className="flex flex-col items-center justify-center gap-3 py-16" data-id="element-137">
            <Spinner size={36} color="#059669" data-id="element-138" />
            <p className="text-xs text-slate-500" data-id="element-139">
              {isLoadingCache ? 'Loading cached totals…' : 'Fetching live totals from Workfront…'}
            </p>
          </div>}

        {/* Table */}
        {!isLoadingCache && !isLoadingTotals && data.focusGroups.length === 0 ? <div className="p-10 text-center text-slate-400 text-sm" data-id="element-140">
            No data available. Sync from Workfront first.
          </div> : !isLoadingCache && !isLoadingTotals ? <div className="overflow-x-auto px-4 py-4" data-id="element-141">
            <table style={{
          borderCollapse: 'collapse',
          width: '100%',
          fontSize: 11
        }} data-id="element-142">
              <thead data-id="element-143">
                <tr data-id="element-144">
                  <th style={{
                ...TH,
                textAlign: 'left',
                width: 90
              }} data-id="element-145">Solution Focus</th>
                  <th style={{
                ...TH,
                textAlign: 'left',
                width: 160
              }} data-id="element-146">Solution</th>
                  {data.segments.length === 0 ? <th style={{
                ...TH,
                width: 120,
                color: '#9CA3AF'
              }} data-id="element-147">Market Area</th> : data.segments.map(s => <th key={s} style={{
                ...TH,
                width: 80
              }} data-id="element-148">{s}</th>)}
                  <th style={{
                ...TH,
                background: '#1F2937',
                width: 80
              }} data-id="element-149">Total</th>
                </tr>
              </thead>
              <tbody data-id="element-150">
                {data.focusGroups.map(({
              focus,
              solutions
            }) => solutions.map((row, i) => <tr key={`${focus}|${row.solution}`} style={{
              background: i % 2 === 0 ? '#fff' : '#FAFAFA'
            }} data-id="element-151">
                      {i === 0 && <td rowSpan={solutions.length} style={{
                ...TD,
                background: '#4B5563',
                color: '#F9FAFB',
                fontWeight: 700,
                fontSize: 10,
                textAlign: 'center',
                verticalAlign: 'middle',
                letterSpacing: '0.03em'
              }} data-id="element-152">
                          {focus}
                        </td>}
                      <td style={{
                ...TD,
                textAlign: 'left',
                fontWeight: 500,
                color: '#374151',
                background: '#F9FAFB'
              }} data-id="element-153">
                        {row.solution}
                      </td>
                      {data.segments.length === 0 ? <td style={TD} data-id="element-154"><span style={{
                  color: '#9CA3AF',
                  fontSize: 10
                }} data-id="element-155">—</span></td> : data.segments.map(seg => <td key={seg} style={TD} data-id="element-156">
                              <Cell cell={row.cells[seg] ?? {
                  num: 0,
                  den: 0,
                  pct: -1
                }} data-id="element-157" />
                            </td>)}
                      <td style={{
                ...TD,
                background: '#F3F4F6'
              }} data-id="element-158">
                        <Cell cell={row.total} data-id="element-159" />
                      </td>
                    </tr>))}
              </tbody>
              <tfoot data-id="element-160">
                <tr style={{
              background: '#F3F4F6'
            }} data-id="element-161">
                  <td colSpan={2} style={{
                ...TD,
                textAlign: 'left',
                fontWeight: 700,
                color: '#374151',
                background: '#E5E7EB'
              }} data-id="element-162">
                    Totals
                  </td>
                  {data.segments.length === 0 ? <td style={TD} data-id="element-163" /> : data.segments.map(seg => <td key={seg} style={TD} data-id="element-164"><Cell cell={data.totals[seg]} data-id="element-165" /></td>)}
                  <td style={{
                ...TD,
                background: '#E5E7EB'
              }} data-id="element-166"><Cell cell={data.grandTotal} data-id="element-167" /></td>
                </tr>
                <tr style={{
              background: '#F9FAFB'
            }} data-id="element-168">
                  <td colSpan={2} style={{
                ...TD,
                textAlign: 'left',
                fontWeight: 700,
                color: '#374151',
                background: '#E5E7EB'
              }} data-id="element-169">
                    % of deals
                  </td>
                  {data.segments.length === 0 ? <td style={TD} data-id="element-170" /> : data.segments.map(seg => <td key={seg} style={{
                ...TD,
                fontWeight: 700,
                color: '#374151',
                fontSize: 11
              }} data-id="element-171">
                          {data.totals[seg].pct >= 0 ? `${Math.round(data.totals[seg].pct)}%` : '—'}
                        </td>)}
                  <td style={{
                ...TD,
                fontWeight: 700,
                color: '#374151',
                background: '#E5E7EB',
                fontSize: 11
              }} data-id="element-172">
                    {data.grandTotal.pct >= 0 ? `${Math.round(data.grandTotal.pct)}%` : '—'}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div> : null}

        {/* Legend */}
        <div className="px-6 py-3 border-t border-slate-100 flex flex-wrap items-center gap-x-5 gap-y-2" data-id="element-173">
          <div className="flex items-center gap-3 text-xs text-slate-600" data-id="element-174">
            <span className="font-semibold text-slate-500" data-id="element-175">Coverage:</span>
            {[{
            bg: '#B91C1C',
            label: '0–20%'
          }, {
            bg: '#EF4444',
            label: '20–40%'
          }, {
            bg: '#FCD34D',
            textColor: '#78350F',
            label: '40–60%'
          }, {
            bg: '#16A34A',
            label: '60–80%'
          }, {
            bg: '#15803D',
            label: '80%+'
          }].map(({
            bg,
            textColor = '#fff',
            label
          }) => <div key={label} className="flex items-center gap-1" data-id="element-176">
                <div style={{
              width: 12,
              height: 12,
              background: bg,
              borderRadius: 2,
              flexShrink: 0
            }} data-id="element-177" />
                <span style={{
              color: textColor === '#fff' ? '#374151' : textColor
            }} data-id="element-178">{label}</span>
              </div>)}
          </div>
          <p className="text-xs text-slate-400 italic ml-auto" data-id="element-179">
            Num = OnD records · Den = total eligible deals · — = no data or &lt;5 volume
          </p>
        </div>
      </div>
    </div>;
}